import { HttpResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Employee } from '../model/employee';
import { OnlineReservationCategory } from '../model/login-response.dto';
import { AuditTrail, Reservation, Table } from '../model/reservation';
import * as EmployeeSelectors from '../store/selectors/employee.selectors';
import { LocalStorageService } from './local-storage.service';

@Injectable({
  providedIn: 'root'
})
export class UtilsService implements OnDestroy {

  destroy$: Subject<boolean> = new Subject<boolean>();
  employee$: Observable<Employee | null> = this.store.pipe(select(EmployeeSelectors.selectLoggedInemployee));
  employee: Employee | null = null;
  private cancelPendingRequests$ = new Subject<void>();

  requestIsInProgress$: Subject<boolean> = new Subject<boolean>();
  requestIsInProgress: boolean = false; 

  changeRestaurantRequested$: Subject<boolean> = new Subject<boolean>();
  changeRestaurantRequested: boolean = false; 

  constructor(private translate: TranslateService,
    private localStorageService: LocalStorageService,
    private store: Store<{ loggedInemployee: Employee }>) {
    const lang = this.translate.currentLang;

    this.initMap(lang === 'el');

    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      this.initMap(event.lang === 'el');
    });

    this.employee$.pipe(takeUntil(this.destroy$)).subscribe((employee) => {
      this.employee = employee;
    });

    this.requestIsInProgress$.pipe(takeUntil(this.destroy$)).subscribe((isInProgress) => {
      this.requestIsInProgress = isInProgress;      
    });
  }

  map: Map<string, string> = new Map();

  getPlatform(): 'web' | 'ios' | 'android' {
    return Capacitor.getPlatform() as any;
  }

  requestFailledDueToNetwork(response: HttpResponse<any>) {
    if (response.body == null) {
      return true;
    }

    if (response != null && response.body.forceLogout == true) return false; // authentication error (ecodes 1000, 1001, 1002, 1003)

    var errorCodes = ["110002", "110005", "4000000002"];

    if (response != null && response.body.Status == false && response.body.ErrorCode != null) { // Received Error Response (reached backend)
      if (errorCodes.indexOf(response.body.ErrorCode) > -1) { // Response.Error code IS contained in "server down" error codes
        return true; // server is down
      }
      else {
        return false; // maybe internal error --> discard
      }
    }
    else {
      return true; // any other case no network error
    }
  }

  /** Cancels all pending Http requests. */
  public cancelPendingRequests() {
    this.cancelPendingRequests$.next()
  }

  public onCancelPendingRequests() {
    return this.cancelPendingRequests$.asObservable()
  }

  fillAuditTrailsDisplayedDiffs(trails: AuditTrail[]) {
    const lang = this.translate.currentLang;

    for (let record of trails) {

      let printedContext = "";

      for (let diff of record.diffs) {

        if (diff.oldValue == "") diff.oldValue = "-";
        if (diff.newValue == "") diff.newValue = "-";

        if (diff.resField == "onlineCategory") {
          let nameOld: string | { en: string, el: string } = "";
          let nameNew: string | { en: string, el: string } = "";

          if (this.isJsonString(diff.oldValue)) {
            nameOld = JSON.parse(diff.oldValue);
          }
          if (this.isJsonString(diff.newValue)) {
            nameNew = JSON.parse(diff.newValue);
          }

          if (lang == "el") {
            if (nameOld != "")
              diff.oldValue = (nameOld as { en: string, el: string }).el;
            if (nameNew != "")
              diff.newValue = (nameNew as { en: string, el: string }).el;
          }
          else {
            if (nameOld != "")
              diff.oldValue = (nameOld as { en: string, el: string }).en;
            if (nameNew != "")
              diff.newValue = (nameNew as { en: string, el: string }).en;
          }
        }

        if (diff.resField == "fixMenu" && this.localStorageService.areMenusAvailable()) {
          if (record.diffs.indexOf(diff) == record.diffs.length - 1) {
            printedContext = printedContext.substring(0, printedContext.length - 2);
          }
          continue;
        }

        const difference = this.map.get(diff.resField) ?? diff.resField; // fallback in case of an unknown field
        if (record.diffs.indexOf(diff) == record.diffs.length - 1) {
          printedContext += '<b>' + difference + '</b>: <span class="text-faded">' + diff.oldValue + " " + "\u2192" + " " + diff.newValue + '</span>';
        }
        else {
          printedContext += '<b>' + difference + '</b>: <span class="text-faded">' + diff.oldValue + " " + "\u2192" + " " + diff.newValue + "</span> <br /> ";
        }
      }

      record.displayedDiffs = printedContext;
    }
  }

  isJsonString(jsonValue: any) {
    try {
      JSON.parse(jsonValue);
    } catch (e) {
      return false;
    }

    return true;
  }

  autoSetOnlineCategory(selectedTables: Table[]): OnlineReservationCategory | null | undefined {
    let str = '';
    let onlineCategory;
    for (var i = 0; i < selectedTables.length; i++) {
      if (str != "") {
        str += ",";
      }

      str += selectedTables[i].tableName;
    }


    if (str != "") {

      let onlineCategoryId = null;

      if (selectedTables[0].onlineCategory != null) {
        onlineCategoryId = selectedTables[0].onlineCategory.id;
      }

      for (let i of this.localStorageService.getOnlineCategoriesHiddenExcluded()) {
        if (i.id == onlineCategoryId) {
          onlineCategory = i;
          break;
        }
      }
    }

    if (str != "") {
      return onlineCategory;
    }

    return undefined;
  }

  calculateBlocking(reservations: Reservation[], withDrinks: boolean) {
    var totalRes = 0;
    var totalCovers = 0;
    var pendingRes = 0;
    var pendingCovers = 0;

    var noCountStatuses = ["Waiting", "Pending", "Cancelled", "NotShown", "WalkOut", "Rejected"];
    var pendingStatuses = ["Confirmed", "LateBooking", "CheckedIn(Partial)"];

    for (let reservation of reservations) {
      if (noCountStatuses.indexOf(reservation.reservationDetails.reservationsStatus) > -1) continue;

      if (withDrinks !== true && reservation.reservationDetails.purpose == "Drink") continue;

      totalRes++;
      totalCovers += reservation.reservationDetails.covers;

      if (pendingStatuses.indexOf(reservation.reservationDetails.reservationsStatus) == -1) continue;

      pendingRes++;
      pendingCovers += reservation.reservationDetails.covers;
    }

    return {
      totalCovers: totalCovers,
      totalRes: totalRes,
      pendingRes: pendingRes,
      pendingCovers: pendingCovers
    }
  }

  initMap(isGr: boolean) {
    this.map = new Map();
    this.map.set("reservationsStatus", this.translate.instant('reservations.auditTrail.reservationsStatus'));
    this.map.set("purpose", this.translate.instant('reservations.auditTrail.purpose'));
    this.map.set("arrivalTime", this.translate.instant('reservations.auditTrail.arrivalTime'));
    this.map.set("covers", this.translate.instant('reservations.auditTrail.covers'));
    this.map.set("reservationType", this.translate.instant('reservations.auditTrail.reservationType'));
    this.map.set("customerType", this.translate.instant('reservations.auditTrail.customerType'));
    this.map.set("surname", this.translate.instant('reservations.auditTrail.surname'));
    this.map.set("name", this.translate.instant('reservations.auditTrail.name'));
    this.map.set("telephone", this.translate.instant('reservations.auditTrail.telephone'));
    this.map.set("mail", this.translate.instant('reservations.auditTrail.mail'));
    this.map.set("onlineCategory", this.translate.instant('reservations.auditTrail.onlineCategory'));
    this.map.set("allocatedTables", this.translate.instant('reservations.auditTrail.allocatedTables'));
    this.map.set("notes", this.translate.instant('reservations.auditTrail.notes'));
    this.map.set("companyTitle", this.translate.instant('reservations.auditTrail.companyTitle'));
    this.map.set("contactSurname", this.translate.instant('reservations.auditTrail.contactSurname'));
    this.map.set("contactName", this.translate.instant('reservations.auditTrail.contactName'));
    this.map.set("contactTelephone", this.translate.instant('reservations.auditTrail.contactTelephone'));
    this.map.set("contactMail", this.translate.instant('reservations.auditTrail.contactMail'));
    this.map.set("fixMenu", this.translate.instant('reservations.auditTrail.fixMenu'));
    this.map.set("selectedMenu", this.translate.instant('reservations.auditTrail.selectedMenu'));
    this.map.set("discount", this.translate.instant('reservations.auditTrail.discount'));
    this.map.set("allergy", this.translate.instant('reservations.auditTrail.allergy'));
    this.map.set("hasCake", this.translate.instant('reservations.auditTrail.hasCake'));
    this.map.set("smokingArea", this.translate.instant('reservations.auditTrail.smokingArea'));
    this.map.set("bill", this.translate.instant('reservations.auditTrail.bill'));
    this.map.set("createdBy", this.translate.instant('reservations.auditTrail.createdBy'));
    this.map.set("confirmedBy", this.translate.instant('reservations.auditTrail.confirmedBy'));
    this.map.set("duration", this.translate.instant('reservations.auditTrail.duration'));
    this.map.set("returnTime", this.translate.instant('reservations.auditTrail.returnTime'));
    this.map.set("deposit", this.translate.instant('reservations.auditTrail.deposit'));
    this.map.set("tokenization", this.translate.instant('reservations.auditTrail.tokenization'));
    this.map.set("tags", this.translate.instant('reservations.auditTrail.tags'));
  }

  mailIsValid(email: string | undefined) {
    if (email == null) {
      return false;
    }
    var re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    return re.test(email);
  }

  initOnlineCategories(onlineCategories: OnlineReservationCategory[]) {

    const lang = this.translate.currentLang;

    for (const onlineCategory of onlineCategories) {
      try {
        const name = JSON.parse(onlineCategory.name ?? '{}');
        onlineCategory.nameLocalized = name[lang];
      } catch (e) {
        onlineCategory.nameLocalized = onlineCategory.name;
      }
    }

    return onlineCategories;
  }

  // Detect if is touch device
  isTouchDevice() {
    return (('ontouchstart' in window) ||
      (navigator.maxTouchPoints > 0) ||
      ((navigator as any).msMaxTouchPoints > 0));
  }

  canWrite(what: string): boolean {
    if (this.employee == null) {
      return false;
    }

    if (this.employee.writePermissions?.some(permission => permission === what)) {
      return true;
    }

    return false;
  }

  canRead(what: string): boolean {
    if (this.employee == null) {
      return false;
    }

    if (this.employee.readPermissions?.some(permission => permission === what)) {
      return true;
    }

    return false;
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
    this.requestIsInProgress$.unsubscribe();
  }

  setRequestIsInProgress(isInProgress: boolean) {   
    if (isInProgress === true) {
      this.requestIsInProgress$.next(true);
    }
    else {
      setTimeout(() => { this.requestIsInProgress$.next(false); }, 100);      
    }
  }

  getRequestIsInProgress(): boolean {
    return this.requestIsInProgress;
  }

  setChangeRestaurantRequested(status: boolean) {   
    this.changeRestaurantRequested = status;
  }

  getChangeRestaurantRequested(): boolean {
    return this.changeRestaurantRequested;
  }

}
